import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Search,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Ticket,
  User,
  LogOut,
  Trophy,
  Music,
  PartyPopper,
  Settings,
  Calendar,
  MapPin,
  Clock
} from 'lucide-react';
import { GAM_TICKET_LOGO } from '@/config/assets';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Live Badge Component
const LiveBadge = () => (
  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase bg-red-500 text-white rounded-full">
    <span className="w-1 h-1 bg-white rounded-full animate-pulse"></span>
    LIVE
  </span>
);

// Compact Fixture Card for dropdown
const CompactFixtureCard = ({ fixture, onClick }) => (
  <div
    onClick={onClick}
    className="flex items-center justify-between p-2.5 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] cursor-pointer transition-all duration-150"
  >
    <div className="flex-1 min-w-0 mr-2">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-medium text-white text-xs truncate max-w-[80px]">{fixture.home_team}</span>
        <span className="text-[9px] text-gray-600">vs</span>
        <span className="font-medium text-white text-xs truncate max-w-[80px]">{fixture.away_team}</span>
        {fixture.is_live && <LiveBadge />}
      </div>
      <div className="flex items-center gap-2 text-[10px] text-gray-500 mt-1">
        <span>{fixture.date}</span>
        <span>•</span>
        <span>{fixture.time}</span>
      </div>
    </div>
    <ChevronRight className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
  </div>
);

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileExpandedItem, setMobileExpandedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [navbarData, setNavbarData] = useState(null);
  const dropdownRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const fetchNavbarData = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/navbar/data`);
        setNavbarData(response.data);
      } catch (error) {
        console.error('Failed to fetch navbar data:', error);
      }
    };
    fetchNavbarData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/events?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setIsMobileMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const tournaments = navbarData?.football?.tournaments || [];

  return (
    <>
      {/* Main Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
          isScrolled
            ? 'bg-[#0a0a0a]/90 backdrop-blur-xl border-b border-white/[0.05]'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg overflow-hidden bg-gradient-to-br from-[#0055FF] to-purple-600">
                <img src={GAM_TICKET_LOGO} alt="Gam Ticket" className="w-full h-full object-cover" />
              </div>
              <div className="hidden xs:flex items-baseline">
                <span className="text-base sm:text-lg font-bold text-white">Gam</span>
                <span className="text-base sm:text-lg font-bold text-[#0055FF]">Ticket</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1" ref={dropdownRef}>
              {/* Football Dropdown */}
              <div className="relative">
                <button
                  onMouseEnter={() => setActiveDropdown('football')}
                  onClick={() => setActiveDropdown(activeDropdown === 'football' ? null : 'football')}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeDropdown === 'football' || location.pathname.startsWith('/football')
                      ? 'text-white bg-white/[0.08]'
                      : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
                  }`}
                >
                  <Trophy className="w-4 h-4" />
                  <span>Football</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${activeDropdown === 'football' ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {activeDropdown === 'football' && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.15 }}
                      onMouseLeave={() => setActiveDropdown(null)}
                      className="absolute top-full left-0 mt-1 w-[480px] p-4 rounded-xl bg-[#0f0f0f]/95 backdrop-blur-xl border border-white/[0.06] shadow-xl"
                    >
                      <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/[0.05]">
                        <h3 className="text-xs font-semibold text-white flex items-center gap-1.5">
                          <Trophy className="w-3.5 h-3.5 text-emerald-400" />
                          Football Zones
                        </h3>
                        <Link to="/football" className="text-[11px] text-[#0055FF] flex items-center gap-0.5">
                          View all <ChevronRight className="w-3 h-3" />
                        </Link>
                      </div>

                      {tournaments.length > 0 ? (
                        <div className="grid grid-cols-2 gap-3">
                          {tournaments.slice(0, 6).map((tournament) => (
                            <div key={tournament.id} className="space-y-1.5">
                              <Link 
                                to={`/football/${tournament.id}`}
                                className="flex items-center gap-1.5 text-xs font-medium text-gray-300 hover:text-white transition-colors"
                              >
                                <span className="text-sm">{tournament.flag || '🇬🇲'}</span>
                                <span>{tournament.name}</span>
                              </Link>
                              <div className="space-y-1 pl-5">
                                {tournament.fixtures?.slice(0, 2).map((fixture) => (
                                  <CompactFixtureCard
                                    key={fixture.id}
                                    fixture={fixture}
                                    onClick={() => {
                                      navigate(`/fixture/${fixture.id}`);
                                      setActiveDropdown(null);
                                    }}
                                  />
                                ))}
                                {(!tournament.fixtures || tournament.fixtures.length === 0) && (
                                  <p className="text-[10px] text-gray-600 py-1">No fixtures</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm text-center py-4">No zones available</p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Concerts */}
              <Link
                to="/concerts"
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  location.pathname.startsWith('/concerts')
                    ? 'text-white bg-white/[0.08]'
                    : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
                }`}
              >
                <Music className="w-4 h-4" />
                <span>Concerts</span>
              </Link>

              {/* Festivals */}
              <Link
                to="/festivals"
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  location.pathname.startsWith('/festivals')
                    ? 'text-white bg-white/[0.08]'
                    : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
                }`}
              >
                <PartyPopper className="w-4 h-4" />
                <span>Festivals</span>
              </Link>
            </div>

            {/* Desktop Search */}
            <form onSubmit={handleSearch} className="hidden md:flex items-center">
              <div className="relative w-40 focus-within:w-52 transition-all duration-200">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-8 pr-3 py-1.5 bg-white/[0.04] border border-white/[0.06] rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#0055FF]/30"
                />
              </div>
            </form>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              {/* Desktop User Menu */}
              <div className="relative hidden sm:block" ref={userMenuRef}>
                {isAuthenticated ? (
                  <>
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="flex items-center gap-1.5 p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.04] transition-all"
                    >
                      <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#0055FF] to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                        {user?.name?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <ChevronDown className={`w-3 h-3 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {isUserMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.15 }}
                          className="absolute right-0 top-full mt-1 w-48 rounded-lg bg-[#0f0f0f]/95 backdrop-blur-xl border border-white/[0.06] shadow-xl overflow-hidden"
                        >
                          <div className="p-3 border-b border-white/[0.05]">
                            <p className="font-medium text-white text-sm truncate">{user?.name}</p>
                            <p className="text-[11px] text-gray-500 truncate">{user?.email}</p>
                          </div>
                          <div className="p-1">
                            <Link
                              to="/my-tickets"
                              onClick={() => setIsUserMenuOpen(false)}
                              className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-gray-400 hover:text-white hover:bg-white/[0.04]"
                            >
                              <Ticket className="w-4 h-4" />
                              My Tickets
                            </Link>
                          </div>
                          <div className="p-1 border-t border-white/[0.05]">
                            <button
                              onClick={handleLogout}
                              className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-red-400 hover:bg-red-500/10"
                            >
                              <LogOut className="w-4 h-4" />
                              Sign Out
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0055FF] hover:bg-[#0066FF] text-white text-sm font-medium transition-all"
                  >
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">Sign In</span>
                  </Link>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.04] transition-all"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
            />
            
            {/* Menu Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed top-0 right-0 bottom-0 w-[260px] bg-[#0a0a0a] border-l border-white/[0.05] z-50 lg:hidden flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-3 border-b border-white/[0.05] flex-shrink-0">
                <Link to="/" className="flex items-center gap-2" onClick={() => setIsMobileMenuOpen(false)}>
                  <div className="w-7 h-7 rounded-md overflow-hidden bg-gradient-to-br from-[#0055FF] to-purple-600">
                    <img src={GAM_TICKET_LOGO} alt="Gam Ticket" className="w-full h-full object-cover" />
                  </div>
                  <span className="font-bold text-white text-sm">GamTicket</span>
                </Link>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.04]"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Search */}
              <form onSubmit={handleSearch} className="p-3 border-b border-white/[0.05] flex-shrink-0">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search events..."
                    className="w-full pl-8 pr-3 py-2 bg-white/[0.04] border border-white/[0.06] rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#0055FF]/30"
                  />
                </div>
              </form>

              {/* Navigation - Scrollable */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-2">
                  {/* Football Accordion */}
                  <div className="border-b border-white/[0.05]">
                    <button
                      onClick={() => setMobileExpandedItem(mobileExpandedItem === 'football' ? null : 'football')}
                      className="w-full flex items-center justify-between px-3 py-3 text-white"
                    >
                      <span className="flex items-center gap-2.5">
                        <Trophy className="w-4 h-4 text-emerald-400" />
                        <span className="font-medium text-sm">Football</span>
                      </span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpandedItem === 'football' ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {mobileExpandedItem === 'football' && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden bg-white/[0.02]"
                        >
                          <div className="px-3 py-2 space-y-1">
                            {tournaments.map((t) => (
                              <Link
                                key={t.id}
                                to={`/football/${t.id}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="flex items-center gap-2 px-2.5 py-2 rounded-md text-xs text-gray-400 hover:text-white hover:bg-white/[0.04]"
                              >
                                <span>{t.flag || '🇬🇲'}</span>
                                <span>{t.name}</span>
                              </Link>
                            ))}
                            <Link
                              to="/football"
                              onClick={() => setIsMobileMenuOpen(false)}
                              className="flex items-center gap-1.5 px-2.5 py-2 rounded-md text-xs text-[#0055FF]"
                            >
                              View all zones <ChevronRight className="w-3 h-3" />
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Concerts */}
                  <Link
                    to="/concerts"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-3 text-white border-b border-white/[0.05]"
                  >
                    <Music className="w-4 h-4 text-purple-400" />
                    <span className="font-medium text-sm">Concerts</span>
                  </Link>

                  {/* Festivals */}
                  <Link
                    to="/festivals"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-3 text-white border-b border-white/[0.05]"
                  >
                    <PartyPopper className="w-4 h-4 text-amber-400" />
                    <span className="font-medium text-sm">Festivals</span>
                  </Link>
                </div>
              </div>

              {/* User Section - Fixed at Bottom */}
              <div className="p-3 border-t border-white/[0.05] bg-[#0a0a0a] flex-shrink-0">
                {isAuthenticated ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2.5 px-2 py-1.5">
                      <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#0055FF] to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                        {user?.name?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white text-sm truncate">{user?.name}</p>
                        <p className="text-[10px] text-gray-500 truncate">{user?.email}</p>
                      </div>
                    </div>
                    <Link
                      to="/my-tickets"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-white bg-white/[0.02] hover:bg-white/[0.04]"
                    >
                      <Ticket className="w-4 h-4" />
                      My Tickets
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-[#0055FF] hover:bg-[#0066FF] text-white font-medium text-sm"
                  >
                    <User className="w-4 h-4" />
                    Sign In
                  </Link>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Spacer */}
      <div className="h-14 sm:h-16" />
    </>
  );
}
